import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { FacetGroup } from '@src/facets';

import type { ScopeMode, WizardState } from './types';

interface ScopeStepProps {
  state: WizardState;
  updateState: (patch: Partial<WizardState>) => void;
  facetGroups: FacetGroup[];
}

const ScopeStep = ({ state, updateState, facetGroups }: ScopeStepProps) => {
  const departmentOptions = facetGroups.find(fg => fg.tag === 'department')?.values.map(v => v.name) ?? [];
  const categoryOptions = facetGroups.find(fg => fg.tag === 'category')?.values.map(v => v.name) ?? [];

  const toggleDepartment = useCallback(
    (name: string) => {
      const current = state.selectedDepartments;
      const next = current.includes(name) ?
        current.filter(d => d !== name) :
        [...current, name];
      updateState({ selectedDepartments: next });
    },
    [state.selectedDepartments, updateState],
  );

  const toggleCategory = useCallback(
    (name: string) => {
      const current = state.selectedCategories;
      const next = current.includes(name) ?
        current.filter(c => c !== name) :
        [...current, name];
      updateState({ selectedCategories: next });
    },
    [state.selectedCategories, updateState],
  );

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Which products should the template include?
      </Typography>

      <RadioGroup
        value={state.scopeMode}
        onChange={(_e, value) => updateState({ scopeMode: value as ScopeMode })}
      >
        <FormControlLabel value="all" control={<Radio />} label="All catalog products" />
        <FormControlLabel value="departments" control={<Radio />} label="Only specific departments / categories" />
        <FormControlLabel value="parentSkus" control={<Radio />} label="Only specific parent SKUs" />
      </RadioGroup>

      {state.scopeMode === 'departments' && (
        <Box sx={{ mt: 2, display: 'flex', gap: 6 }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Departments
            </Typography>
            {departmentOptions.map(name => (
              <FormControlLabel
                key={name}
                control={
                  <Checkbox
                    checked={state.selectedDepartments.includes(name)}
                    onChange={() => toggleDepartment(name)}
                    size="small"
                  />
                }
                label={name}
                sx={{ display: 'block' }}
              />
            ))}
          </Box>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Categories
            </Typography>
            {categoryOptions.map(name => (
              <FormControlLabel
                key={name}
                control={
                  <Checkbox
                    checked={state.selectedCategories.includes(name)}
                    onChange={() => toggleCategory(name)}
                    size="small"
                  />
                }
                label={name}
                sx={{ display: 'block' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {state.scopeMode === 'parentSkus' && (
        <Box sx={{ mt: 2 }}>
          <TextField
            label="Parent SKUs"
            placeholder="Enter parent SKUs separated by commas or newlines"
            InputLabelProps={{ shrink: true }}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            size="small"
            value={state.parentSkuInput}
            onChange={e => updateState({ parentSkuInput: e.target.value })}
          />
        </Box>
      )}
    </Box>
  );
};

export default ScopeStep;
